// @ts-strict-ignore
import { connect, Provider } from 'react-redux'
import { applyMiddleware, createStore } from 'redux'
import { HelmetProvider, Helmet } from 'react-helmet-async'
import { ThemeProvider } from 'theme-ui'
import React from 'react'
import thunk from 'redux-thunk'
import { RootEVEntry } from './components/helpers/root-entry'
import EVCoreProvider from './components/helpers/provider'
import EVCoreDefaultOptions from './components/helpers/options'
import ArticleSchemaConnected from './components/article-schema'
import BreadcrumbSchema from './components/breadcrumb-schema'
import { useDataSelector, useEntity, useMeta } from './components/helpers/selectors'
import { robotsContent, canonicalHref } from './components/helpers/meta-tags'
import { cf2imgix } from './components/helpers/imgix-image'
import getImageHost from './utils/get-image-host'
import { Components as components } from './components/helpers/component-map'

import { meta, theme, router, location, trustpilot, brand, pathname } from './redux/app-selectors'
import {
  breadcrumb,
  dates,
  includes,
  url,
  rootEntity,
  hydrations,
} from './redux/contentful-selectors'
import reducer from './redux/reducer'
import ProductSchema from './components/product-schema'

const ConnectedThemeProvider = connect((s) => ({
  theme: theme(s),
}))(ThemeProvider)

const MAX_TITLE_LENGTH = 70

const HeadContent = ({ meta, rootEntity, howToSchema, trustpilot, path, faqSchema }) => {
  const { breadcrumb, url } = useDataSelector()
  const linkedPage = useEntity(rootEntity.fields.link)
  const metaImage = useEntity(linkedPage.metaImage)
  const ogImage = useEntity(linkedPage.ogImage)
  const twitterAccount = useMeta('twitterAccount')
  const imageHost = getImageHost()

  const titleStr = linkedPage.title || ''
  const title = titleStr.length > MAX_TITLE_LENGTH ? titleStr : `${titleStr} | ${meta.title}`
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <link
          rel='icon'
          type={meta.favicon.type}
          href={meta.favicon.href}
          sizes={meta.favicon.sizes}
        />
        <link href={canonicalHref(url)} rel='canonical' />
        <link href={`//${imageHost}`} rel='dns-prefetch' />
        <link href={`https://${imageHost}`} rel='preconnect' crossorigin />
        <link href='//images.ctfassets.net' rel='dns-prefetch' />
        <link href='https://images.ctfassets.net' rel='preconnect' crossorigin />

        <meta name='robots' content={robotsContent(rootEntity.fields)} />
        <meta name='description' content={linkedPage.metaDescription} />
        <meta property='og:title' content={linkedPage.ogTitle || title} />
        <meta property='og:type' content={linkedPage.ogType || 'website'} />
        <meta
          property='og:description'
          content={linkedPage.ogDescription || linkedPage.metaDescription}
        />
        <meta property='og:url' content={canonicalHref(url)} />
        <Choose>
          <When condition={ogImage.sys}>
            <meta property='og:image' content={cf2imgix(ogImage.file.url)} />
            <meta property='og:image:secure_url' content={cf2imgix(ogImage.file.url)} />
          </When>
          <When condition={metaImage.sys}>
            <meta property='og:image' content={cf2imgix(metaImage.file.url)} />
            <meta property='og:image:secure_url' content={cf2imgix(metaImage.file.url)} />
          </When>
        </Choose>
        <If condition={twitterAccount}>
          <meta property='twitter:site' content={twitterAccount} />
          <meta property='twitter:card' content='summary_large_image' />
        </If>
      </Helmet>
      <BreadcrumbSchema breadcrumb={breadcrumb} url={url} />
      <ArticleSchemaConnected page={linkedPage} url={rootEntity.fields} meta={meta} />
      {howToSchema?.map((schema, index) => (
        <Helmet key={index}>
          <script type='application/ld+json'>{JSON.stringify(schema)}</script>
        </Helmet>
      ))}
      {faqSchema && (
        <Helmet>
          <script type='application/ld+json'>{faqSchema}</script>
        </Helmet>
      )}
      <ProductSchema {...trustpilot} path={path} />
    </>
  )
}

export default ({
  isServer,
  initialState,
  helmetContext = {},
  experiences: __experiences,
  faqSchema,
}) => {
  const state = isServer ? initialState : window.__initialState__
  const experiences = isServer ? __experiences : window.__experiences__
  const fsCoreState = { ...state }

  const store = createStore(reducer, fsCoreState, applyMiddleware(thunk))

  return (
    <EVCoreProvider
      data={{
        breadcrumb: breadcrumb(state),
        dates: dates(state),
        includes: includes(state),
        rootEntity: rootEntity(state),
        meta: meta(state),
        router: router(state),
        location: location(state),
        url: url(state),
        toHydrate: hydrations(state),
      }}
      tracking={
        isServer
          ? undefined
          : {
              client: window.nerdalytics,
            }
      }
      components={components}
      options={EVCoreDefaultOptions}
      experiences={experiences}
    >
      <HelmetProvider context={helmetContext}>
        <Provider store={store}>
          <ConnectedThemeProvider theme={theme(state)}>
            <HeadContent
              rootEntity={rootEntity(state)}
              meta={meta(state)}
              howToSchema={state.components.howToSchema}
              trustpilot={trustpilot(state, brand(state))}
              path={pathname(state)}
              faqSchema={faqSchema}
            />
            <RootEVEntry {...rootEntity(state)} />
          </ConnectedThemeProvider>
        </Provider>
      </HelmetProvider>
    </EVCoreProvider>
  )
}
